import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiX } = FiIcons;

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedImage, setSelectedImage] = useState(null);

  const categories = ['All', 'Residential', 'Commercial', 'Modern', 'Traditional'];

  const projects = [
    {
      id: 1,
      title: 'Modern Living Room',
      category: 'Residential',
      style: 'Modern',
      image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      description: 'A sleek and contemporary living space with clean lines and neutral tones.'
    },
    {
      id: 2,
      title: 'Executive Office',
      category: 'Commercial',
      style: 'Modern',
      image: 'https://images.unsplash.com/photo-1497366216548-37526070297c?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      description: 'Professional office design that promotes productivity and reflects company values.'
    },
    {
      id: 3,
      title: 'Luxury Bedroom',
      category: 'Residential',
      style: 'Traditional',
      image: 'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      description: 'Elegant bedroom design with rich textures and sophisticated color palette.'
    },
    {
      id: 4,
      title: 'Contemporary Kitchen',
      category: 'Residential',
      style: 'Modern',
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      description: 'Functional and stylish kitchen with state-of-the-art appliances.'
    },
    {
      id: 5,
      title: 'Restaurant Interior',
      category: 'Commercial',
      style: 'Modern',
      image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      description: 'Warm and inviting restaurant space designed for optimal customer experience.'
    },
    {
      id: 6,
      title: 'Classic Study Room',
      category: 'Residential',
      style: 'Traditional',
      image: 'https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      description: 'Traditional study room with rich wood finishes and classic furnishings.'
    },
    {
      id: 7,
      title: 'Minimalist Bathroom',
      category: 'Residential',
      style: 'Modern',
      image: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      description: 'Clean and serene bathroom design with modern fixtures and finishes.'
    },
    {
      id: 8,
      title: 'Retail Store',
      category: 'Commercial',
      style: 'Modern',
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      description: 'Modern retail space designed to enhance product visibility and customer flow.'
    },
    {
      id: 9,
      title: 'Traditional Dining Room',
      category: 'Residential',
      style: 'Traditional',
      image: 'https://images.unsplash.com/photo-1615529328331-f8917597711f?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80',
      description: 'Elegant dining room with traditional elements and formal atmosphere.'
    }
  ];

  const filteredProjects = selectedCategory === 'All' 
    ? projects 
    : projects.filter(project => 
        project.category === selectedCategory || project.style === selectedCategory
      );

  return (
    <>
      <Helmet>
        <title>Portfolio - Elite Interior Design | Our Best Interior Design Projects</title>
        <meta name="description" content="Browse our portfolio of stunning interior design projects including residential homes, commercial spaces, modern and traditional designs. See our work and get inspired." />
        <meta name="keywords" content="interior design portfolio, design projects, residential interiors, commercial interiors, modern design, traditional design" />
      </Helmet>

      <div className="pt-20">
        {/* Hero Section */}
        <section className="py-16 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center"
            >
              <h1 className="text-5xl font-bold text-white mb-6">Our Portfolio</h1>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Explore our collection of stunning interior design projects that showcase 
                our creativity, attention to detail, and commitment to excellence.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Filter Buttons */}
        <section className="py-8 bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap justify-center gap-4">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-2 rounded-full transition-colors duration-300 ${
                    selectedCategory === category
                      ? 'bg-yellow-500 text-black font-bold'
                      : 'bg-gray-700 text-white hover:bg-gray-600'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Portfolio Grid */}
        <section className="py-16 bg-gray-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <motion.div 
              layout
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            >
              <AnimatePresence>
                {filteredProjects.map((project) => (
                  <motion.div
                    key={project.id}
                    layout
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.3 }}
                    className="bg-gray-700 rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 cursor-pointer"
                    onClick={() => setSelectedImage(project)}
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-64 object-cover hover:scale-110 transition-transform duration-300"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                        <span className="text-white text-lg font-semibold opacity-0 hover:opacity-100 transition-opacity duration-300">
                          View Details
                        </span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-xl font-bold text-white mb-2">{project.title}</h3>
                      <div className="flex space-x-2 mb-3">
                        <span className="bg-yellow-500 text-black text-xs px-2 py-1 rounded">
                          {project.category}
                        </span>
                        <span className="bg-gray-600 text-white text-xs px-2 py-1 rounded">
                          {project.style}
                        </span>
                      </div>
                      <p className="text-gray-300 text-sm">{project.description}</p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </motion.div>
          </div>
        </section>

        {/* Modal */}
        <AnimatePresence>
          {selectedImage && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
              onClick={() => setSelectedImage(null)}
            >
              <motion.div
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.8 }}
                className="relative max-w-4xl max-h-full"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setSelectedImage(null)}
                  className="absolute -top-12 right-0 text-white hover:text-yellow-400 transition-colors"
                >
                  <SafeIcon icon={FiX} className="h-8 w-8" />
                </button>
                <img
                  src={selectedImage.image}
                  alt={selectedImage.title}
                  className="w-full h-auto rounded-lg"
                />
                <div className="bg-gray-800 p-6 rounded-b-lg">
                  <h3 className="text-2xl font-bold text-white mb-2">{selectedImage.title}</h3>
                  <div className="flex space-x-2 mb-3">
                    <span className="bg-yellow-500 text-black text-sm px-3 py-1 rounded">
                      {selectedImage.category}
                    </span>
                    <span className="bg-gray-600 text-white text-sm px-3 py-1 rounded">
                      {selectedImage.style}
                    </span>
                  </div>
                  <p className="text-gray-300">{selectedImage.description}</p>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-yellow-500 to-yellow-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl font-bold text-black mb-4">Love What You See?</h2>
              <p className="text-black mb-8 text-lg">
                Let's create something beautiful together. Contact us to start your project.
              </p>
              <a
                href="#contact"
                className="inline-block bg-black hover:bg-gray-800 text-white font-bold py-3 px-8 rounded-lg transition-colors duration-300"
              >
                Start Your Project
              </a>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Portfolio;